// ./src/components/features/DisplayConfigurations/ScheduleTypeSelector/ScheduleTypeSelector.tsx
import React, { useEffect } from "react";
import { Select, SelectProps } from "@mantine/core";

export type BlockTransitionAnimationType =
  | "fade"
  | "slide-left"
  | "slide-right"
  | "slide-up"
  | "slide-down"
  | "scale"
  | "scale-rotate"
  | "flip"
  | "flip-x"
  | "zoom-out"
  | "push-left"
  | "push-right"
  | "curtain"
  | "blur"
  | "bounce"
  | "none";

const BLOCK_TRANSITION_ANIMATION_OPTIONS = [
  { value: "fade", label: "Fade", disabled: false },
  { value: "slide-left", label: "Slide Left", disabled: false },
  { value: "slide-right", label: "Slide Right", disabled: false },
  { value: "slide-up", label: "Slide Up", disabled: false },
  { value: "slide-down", label: "Slide Down", disabled: false },
  { value: "scale", label: "Scale", disabled: false },
  { value: "scale-rotate", label: "Scale Rotate", disabled: false },
  { value: "flip", label: "Flip", disabled: false },
  { value: "flip-x", label: "Flip X", disabled: false },
  { value: "zoom-out", label: "Zoom Out", disabled: false },
  { value: "push-left", label: "Push Left", disabled: false },
  { value: "push-right", label: "Push Right", disabled: false },
  { value: "curtain", label: "Curtain", disabled: false },
  { value: "blur", label: "Blur", disabled: false },
  { value: "bounce", label: "Bounce", disabled: false },
  { value: "none", label: "None", disabled: false },
];

export interface BlockTransitionAnimationSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Current status value (100 for enabled, 0 for disabled) */
  value: string | null;

  /** Callback when status changes */
  onChange: (value: string | null) => void;

  required?: boolean;
}

export const BlockTransitionAnimationSelector: React.FC<
  BlockTransitionAnimationSelectorProps
> = ({
  value,
  onChange,
  required,
  placeholder = "Select transtion animation",
  ...selectProps
}) => {
  // Convert numeric value to string for Select component
  const stringValue = value !== null ? String(value) : null;

  // Prepare select data with optional descriptions
  const selectData = BLOCK_TRANSITION_ANIMATION_OPTIONS.map((option: any) => {
    let label: string = option.label;

    return {
      value: option.value,
      label,
      disabled: option.disabled,
    };
  });

  // Auto-select first display type if required and no value is set
  useEffect(() => {
    if (required && !value && onChange) {
      const firstOption = BLOCK_TRANSITION_ANIMATION_OPTIONS[0];
      if (firstOption && !firstOption.disabled) {
        onChange(firstOption.value);
      }
    }
  }, [required, value, onChange]);

  // Handle change and convert back to number
  const handleChange = (stringValue: string | null) => {
    if (stringValue === null) {
      onChange(null);
    } else {
      onChange(stringValue);
    }
  };

  return (
    <Select
      {...selectProps}
      data={selectData}
      value={stringValue}
      onChange={handleChange}
      placeholder={placeholder}
      clearable={!required}
      required={required}
      allowDeselect={!required}
      withAsterisk={required}
      size="md"
    />
  );
};

// Export status options for use in other components
export { BLOCK_TRANSITION_ANIMATION_OPTIONS };
